<template>
  <div>
    <v-card elevation="0" class="ma-sm-5 pa-5">
      <v-card-text>
        <v-row>
          <v-col class="px-3">
            <v-row no-gutters align="center">
              <h5 class="greyBold--text font-weight-bold mb-0">{{ $t('settings/profile:customer.sub-customers.title') }}</h5>
            </v-row>
          </v-col>
          <v-spacer></v-spacer>
          <v-col class="text-right">
            <BaseButton
                color="primary"
                :btn-text="$t('settings/profile:customer.sub-customers.add')"
                icon="add"
                @clickedButton="openAddCustomerDialog"
            />
          </v-col>
          <v-col cols="12" class="pt-0" >

            <DataTable
                :headers="headers"
                :data="restSubCustomers"
                enabled-search
                icon="edit"
                @clickedButton1="editSubCustomer($event)"
                sortBy="name"
            ></DataTable>
          </v-col>
        </v-row>

        <v-form ref="excelForm">
          <h4 class="primary--text font-weight-bold">
            {{ $t('settings/profile:customer.sub-customers.import-label')}}</h4>
          <v-file-input
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :label="$t('settings/profile:customer.sub-customers.import-excel-file')"
              show-size
              truncate-length="100"
              v-model="excelFile"
              :rules="[ruleRequired($t('utils:rules.requiredFile'))]"
          />
          <div class="horizontal-container">
            <BaseButton
                :disabled="isImporting"
                class="mr-3 mb-3 mb-md-0 white--text"
                color="primary"
                :btn-text="$t('settings/profile:customer.sub-customers.import-button')"
                @clickedButton="startExcelImport"
            />

            <v-checkbox
                v-model="allowOverride"
                :label="$t('settings/profile:customer.sub-customers.import-override')"
                color="secondary"
                class="mt-0 ml-2"
            >

            </v-checkbox>
          </div>
        </v-form>

      </v-card-text>
    </v-card>

    <MinimizedCustomerDialogForm ref="customerDialogForm" sub-customer @onSave="onSave" />
    <ImportLogDialog ref="logDialog" :log="importLog" :title="$t('settings/profile:customer.sub-customers.import-log.title')"></ImportLogDialog>
  </div>
</template>

<script>
import MinimizedCustomerDialogForm from '@/modules/core/components/MinimizedCustomerDialogForm';
import DataTable from "@/modules/core/components/DataTable/DataTable.vue";
import BaseButton from "@/modules/core/components/Base/BaseButton.vue";
import CustomerService from "@/modules/core/services/customer-service";
import DateUtils from "@/modules/core/services/date-utils";
import Utils from "@/modules/core/services/utils";
import {ruleRequired} from "@/modules/core/services/rules";
import ImportService from "@/modules/book/services/import-service";
import ImportLogDialog from "@/modules/core/components/ImportLogDialog.vue";

export default {
  name: "SubCustomers",
  components: {ImportLogDialog, DataTable, MinimizedCustomerDialogForm, BaseButton},
  data() {
    return {
      restSubCustomers: [],
      excelFile: null,
      isImporting: false,
      allowOverride: false,
      importLog: "",
    }
  },
  computed: {
    headers() {
       return [
         {text: this.$t('common:name'), value: "name"},
         {text: this.$t('common:street'), value: "street"},
         {text: this.$t('common:zip'), value: "zip"},
         {text: this.$t('common:city'), value: "city"},
         {text: this.$t('settings/customer:common.contact-name'), value: "contactName"},
         {text: this.$t('settings/customer:common.contact-mail'), value: "contactMail"},
         {text: this.$t('settings/customer:common.contact-phone'), value: "contactPhone"},
         {text: this.$t('common:created_at'), value: 'createdAt', sort: this.compareDate },
         {text: "", value: "controls", sortable: false},
      ];
    }
  },
  created() {
    this.updateSubCustomerTable();
  },
  methods: {
    ruleRequired,
    openAddCustomerDialog() {
      this.$refs.customerDialogForm.openAddDialog();
    },
    editSubCustomer(subCustomer) {
      try {
        let customer = JSON.parse( JSON.stringify(subCustomer)); // copy fields
        this.$refs.customerDialogForm.openEditDialog(customer);
      } catch {
        this.$logger.error("error JSON.parse() in editSubCustomer(subCustomer)");
      }
    },
    onSave() {
      this.updateSubCustomerTable();
    },

    updateSubCustomerTable() {
      CustomerService.readAllSubCustomersFromAPI(
          (result) => {
            if (result.data) {
              this.restSubCustomers = result.data;
            }
            else {
              Utils.showFailureSnackbar(this.$t('settings/profile:customer.sub-customers.snackbar.customer-load-failure'));
              this.restSubCustomers = [];
            }
          },
      () => {
          Utils.showFailureSnackbar(this.$t('settings/profile:customer.sub-customers.snackbar.customer-load-failure'));
          this.restSubCustomers = [];
        }
      );
    },

    startExcelImport() {
      if (!this.$refs.excelForm.validate()) {
        return;
      }
      this.isImporting = true;
      ImportService.importSubCustomerExcel(this.excelFile, this.allowOverride,
          (response) => {
            this.importLog = response.data;
            this.showLogAndUpdate();
          },
          (service, error) => {
            if (error && error.response && error.response.data) {
              this.importLog =  error.response.data;
            }
            else {
              this.importLog = error.toString();
            }
            this.showLogAndUpdate();
          }
      )
    },

    showLogAndUpdate() {
      this.$refs.logDialog.openLogDialog();
      this.updateSubCustomerTable();
      this.excelFile = null;
      this.$refs.excelForm.resetValidation();
      this.isImporting = false;
    },
    /**
     *  compare function for date sorting used in v-data-table headers
     * @param date1 first date to compare
     * @param date2 second date to compare
     * @returns {number|number} -1 / 0 / 1  on first date before / equal / later than second
     */
    compareDate(date1,date2) {
      try {
        let iso1 = DateUtils.parseDateForVuetify(date1); // converts to format yyyy-MM-dd
        let iso2 = DateUtils.parseDateForVuetify(date2);
        if (iso1 ===  iso2 ) {
          return 0;
        } else if (iso1 === null) {
          return -1;
        } else if (iso2 === null) {
          return 1;
        } else {
          return iso1 < iso2 ? -1 : iso1 > iso2 ? 1 : 0;
        }
      } catch (ex) { // fail-save in sorting for invalid format
        return 0;
      }
    }
  },


}
</script>

<style scoped>
.horizontal-container {
  display: flex;
  flex-direction: row;
  align-content: center;
}
</style>